<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { format } from "date-fns";
import pluralize from "pluralize";
import Button from "primevue/button";
import Tag from "primevue/tag";
import Skeleton from "primevue/skeleton";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import SharedEntityDrawer from "src/components/shared/SharedEntityDrawer.vue";
import SharedCopyableField from "src/components/shared/SharedCopyableField.vue";
import SharedRelationCard from "src/components/shared/SharedRelationCard.vue";
import { SchemaType } from "src/enums/schema-types";
import type { CrudRouteMeta, CrudTableColumn, TableColumns } from "src/router/types";
import type { EntitySchema } from "src/types/entity-schema";
import { formModelRegistry } from "src/api/form/form-models";
import { entityDisplayKeys } from "src/router/entity-display-keys";

type FieldType = "text" | "date" | "boolean";

const route = useRoute();
const router = useRouter();
const routeMeta = route.meta as CrudRouteMeta;
const entityId = computed(() => route.params.id as string);

const { data, isPending, isError } = routeMeta.requests.fetchOne!(entityId);

const entitySchema: EntitySchema = {
  entity: routeMeta.entityName,
  requests: routeMeta.requests,
  formSchema: routeMeta.formSchema,
  crud: routeMeta.crud,
  validationSchema: routeMeta.validationSchema,
};

const isDrawerVisible = ref(false);
const drawerType = ref<SchemaType>(SchemaType.UPDATE);
const collapsedSections = ref<Record<string, boolean>>({});

const columns = computed(() => routeMeta.tableColumns as TableColumns);

function isIdField(key: string): boolean {
  return key === "id" || key.endsWith("_id");
}

const allScalarFields = computed(() => {
  if (!data.value) return [];
  return Object.entries(data.value as Record<string, unknown>)
    .filter(([key, value]) => {
      if (isIdField(key)) return false;
      if (Array.isArray(value)) return false;
      if (typeof value === "object" && value !== null && "id" in (value as Record<string, unknown>))
        return false;
      return true;
    })
    .map(([key, value]) => ({
      key,
      label: formatLabel(key),
      value,
      column: columns.value[key] as CrudTableColumn | undefined,
    }));
});

const badgeFields = computed(() =>
  allScalarFields.value.filter((f) => {
    const col = f.column;
    if (!col?.options || !Array.isArray(col.options) || col.options.length === 0) return false;
    return f.value !== null && f.value !== undefined;
  }),
);

const detailFields = computed(() => {
  const badgeKeys = new Set(badgeFields.value.map((f) => f.key));
  return allScalarFields.value
    .filter((f) => !badgeKeys.has(f.key))
    .map((f) => {
      let fieldType: FieldType = "text";
      if (typeof f.value === "boolean") fieldType = "boolean";
      else if (f.column?.format === "date" || f.column?.format === "date-time")
        fieldType = "date";
      return { ...f, fieldType };
    });
});

const relationFields = computed(() => {
  if (!data.value) return [];
  return Object.entries(data.value as Record<string, unknown>)
    .filter(([key, value]) => {
      if (isIdField(key)) return false;
      return (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        "id" in (value as Record<string, unknown>)
      );
    })
    .map(([key, value]) => {
      const obj = value as Record<string, unknown>;
      return {
        key,
        label: formatLabel(key),
        id: obj.id as string,
        embeddedData: obj,
        entityRoute: resolveEntityRoute(key),
      };
    });
});

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Render a nested object as a human-readable label by joining the values of
 * the schema's display-key fields. Uses codegen-emitted entityDisplayKeys.
 * Falls back to `name`, `label`, or the id when the schema is unknown.
 */
function formatNestedObject(
  value: Record<string, unknown>,
  schemaName: string | undefined,
): string {
  const keys = schemaName ? entityDisplayKeys[schemaName] : undefined;
  if (keys && keys.length > 0) {
    const parts = keys
      .map((k) => value[k])
      .filter((v) => v !== null && v !== undefined && v !== "")
      .map((v) => String(v));
    if (parts.length > 0) return parts.join(" ");
  }
  if (typeof value.name === "string" && value.name) return value.name;
  if (typeof value.label === "string" && value.label) return value.label;
  return String(value.id ?? "-");
}

/**
 * For an array column on the current entity, return the schema name of each
 * row item (via the column's ref). Used to drive per-cell ref chains.
 */
function arrayItemSchema(arrayFieldKey: string): string | undefined {
  return columns.value[arrayFieldKey]?.ref;
}

/**
 * For a cell within a row of a known item-schema, return the schema name of
 * the cell value (via that field's ref in the form-model registry).
 */
function cellSchema(itemSchemaName: string | undefined, cellKey: string): string | undefined {
  if (!itemSchemaName) return undefined;
  return formModelRegistry[itemSchemaName]?.[cellKey]?.ref;
}

const arrayFields = computed(() => {
  if (!data.value) return [];
  return Object.entries(data.value as Record<string, unknown>)
    .filter(([, value]) => Array.isArray(value) && (value as unknown[]).length > 0)
    .map(([key, value]) => {
      const rawItems = value as unknown[];
      const isObjectArray = isPlainObject(rawItems[0]);
      const items = isObjectArray ? (rawItems as Record<string, unknown>[]) : [];
      const itemColumns = isObjectArray
        ? Object.keys(rawItems[0] as object).filter((col) => !isIdField(col))
        : [];
      const primitiveItems = isObjectArray
        ? []
        : (rawItems as string[]).map((id) => ({ id: String(id) }));
      const entityRoute = resolveEntityRoute(pluralize.singular(key));
      return {
        key,
        label: formatLabel(key),
        items,
        columns: itemColumns,
        primitiveItems,
        isObjectArray,
        count: rawItems.length,
        entityRoute,
      };
    });
});

function toggleSection(key: string) {
  collapsedSections.value[key] = !collapsedSections.value[key];
}

function formatLabel(fieldName: string): string {
  return fieldName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(value: unknown, column?: CrudTableColumn): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (column?.format === "date" && typeof value === "string") {
    try {
      return format(new Date(value), "d MMM yyyy");
    } catch {
      return String(value);
    }
  }

  if (column?.format === "date-time" && typeof value === "string") {
    try {
      return format(new Date(value.replace(" ", "T")), "d MMM yyyy HH:mm");
    } catch {
      return String(value);
    }
  }

  if (column?.format === "currency" || column?.format === "float") {
    const num = Number(value);
    if (!isNaN(num)) {
      return column.format === "currency"
        ? new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(num)
        : new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 2 }).format(num);
    }
  }

  if (typeof value === "object" && value !== null) {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return "-";
    return entries.map(([k, v]) => `${formatLabel(k)}: ${v ?? "-"}`).join(", ");
  }

  return String(value);
}

function resolveEntityRoute(fieldKey: string): string | null {
  const baseName = fieldKey.replace(/_id$/, "").replace(/_/g, "-");
  const pluralName = pluralize(baseName);
  if (router.hasRoute(`${pluralName}-detail`)) return `${pluralName}-detail`;
  if (router.hasRoute(pluralName)) return pluralName;
  return null;
}

/**
 * Strip codegen prefixes/suffixes from a schema name and derive an entity
 * route. Lets cells whose column key does not match the target entity
 * (e.g. `created_by_user` -> NestedUserResponse -> `users-detail`) still
 * resolve a destination.
 */
function resolveRouteFromSchema(schemaName: string | undefined): string | null {
  if (!schemaName) return null;
  const core = schemaName
    .replace(/^(Fetch|Nested)/, "")
    .replace(/(Lite|Detail|Response\d*)+$/g, "")
    .replace(/WithContac?tDetail$/, "");
  if (!core) return null;
  const kebab = core.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
  const pluralName = pluralize(kebab);
  if (router.hasRoute(`${pluralName}-detail`)) return `${pluralName}-detail`;
  if (router.hasRoute(pluralName)) return pluralName;
  return null;
}

function getCellRelationRoute(
  arrayKey: string,
  cellKey: string,
  value: unknown,
): string | null {
  if (!isPlainObject(value) || !("id" in value)) return null;
  return (
    resolveEntityRoute(cellKey) ??
    resolveRouteFromSchema(cellSchema(arrayItemSchema(arrayKey), cellKey))
  );
}

function navigateToCellRelation(arrayKey: string, cellKey: string, value: unknown) {
  if (!isPlainObject(value)) return;
  const id = value.id;
  if (typeof id !== "string" || !id) return;
  const targetRoute = getCellRelationRoute(arrayKey, cellKey, value);
  if (!targetRoute) return;
  if (targetRoute.endsWith("-detail")) {
    router.push({ name: targetRoute, params: { id } });
  } else {
    router.push({ name: targetRoute, query: { uuid: id } });
  }
}

function navigateToRelation(relation: { entityRoute: string | null; id: string }) {
  if (relation.entityRoute && router.hasRoute(relation.entityRoute)) {
    if (relation.entityRoute.endsWith("-detail")) {
      router.push({ name: relation.entityRoute, params: { id: relation.id } });
    } else {
      router.push({ name: relation.entityRoute, query: { uuid: relation.id } });
    }
  }
}

function navigateToArrayEntity(key: string, item: Record<string, unknown>) {
  const entityRoute = resolveEntityRoute(pluralize.singular(key));
  if (entityRoute && item.id) {
    if (entityRoute.endsWith("-detail")) {
      router.push({ name: entityRoute, params: { id: item.id as string } });
    } else {
      router.push({ name: entityRoute, query: { uuid: item.id as string } });
    }
  }
}

function onClickEdit() {
  drawerType.value = SchemaType.UPDATE;
  isDrawerVisible.value = true;
}

function onClickBack() {
  const listRouteName = route.name?.toString().replace(/-detail$/, "");
  if (listRouteName && router.hasRoute(listRouteName)) {
    router.push({ name: listRouteName });
  } else {
    router.back();
  }
}
</script>

<template>
  <div class="p-4">
    <!-- Loading State -->
    <div v-if="isPending" class="flex flex-col gap-3">
      <Skeleton width="100%" height="4.5rem" border-radius="0.5rem" />
      <Skeleton width="100%" height="16rem" border-radius="0.5rem" />
      <Skeleton width="100%" height="8rem" border-radius="0.5rem" />
    </div>

    <!-- Error State -->
    <div
      v-else-if="isError"
      class="surface-card border-round p-6 flex flex-col items-center gap-3"
    >
      <i class="pi pi-exclamation-triangle text-4xl text-red-400" />
      <p class="text-color-secondary m-0">Failed to load entity data.</p>
      <Button label="Go Back" icon="pi pi-arrow-left" text @click="onClickBack" />
    </div>

    <!-- Main Content -->
    <div v-else-if="data" class="flex flex-col gap-3">
      <!-- Header Card -->
      <div class="surface-card border-round p-4">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-3 min-w-0">
            <Button
              icon="pi pi-arrow-left"
              text
              rounded
              severity="secondary"
              @click="onClickBack"
              class="shrink-0"
            />
            <h1 class="text-xl font-semibold m-0 capitalize">
              {{ routeMeta.entityName }}
            </h1>
          </div>
          <Button
            v-if="routeMeta.crud.update"
            label="Edit"
            icon="pi pi-pencil"
            @click="onClickEdit"
            class="shrink-0"
          />
        </div>
        <div
          v-if="badgeFields.length > 0"
          class="flex items-center gap-3 mt-3 ml-11 flex-wrap"
        >
          <Tag
            v-for="badge in badgeFields"
            :key="badge.key"
            :value="String(badge.value)"
            severity="secondary"
            class="capitalize"
          />
        </div>
      </div>

      <!-- Detail Fields -->
      <div v-if="detailFields.length > 0" class="surface-card border-round p-4">
        <h2 class="detail-section-title">Details</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          <div v-for="field in detailFields" :key="field.key" class="min-w-0">
            <div class="detail-field-label">{{ field.label }}</div>
            <Tag
              v-if="field.fieldType === 'boolean'"
              :value="field.value ? 'Yes' : 'No'"
              :severity="field.value ? 'success' : 'secondary'"
            />
            <div v-else class="font-medium">
              <SharedCopyableField
                v-if="formatValue(field.value, field.column) !== '-'"
                :value="formatValue(field.value, field.column)"
              />
              <span v-else class="text-color-secondary">-</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Relations -->
      <div v-if="relationFields.length > 0" class="surface-card border-round p-4">
        <h2 class="detail-section-title">Relations</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <SharedRelationCard
            v-for="relation in relationFields"
            :key="relation.key"
            :label="relation.label"
            :id="relation.id"
            :embedded-data="relation.embeddedData"
            :entity-route="relation.entityRoute"
            :hide-fields="['id']"
            @navigate="navigateToRelation(relation)"
            stacked
          />
        </div>
      </div>

      <!-- Array Sections -->
      <div
        v-for="arr in arrayFields"
        :key="arr.key"
        class="surface-card border-round overflow-hidden"
      >
        <button
          type="button"
          class="detail-array-header"
          @click="toggleSection(arr.key)"
        >
          <div class="flex items-center gap-2">
            <i
              class="pi text-xs transition-transform duration-200"
              :class="
                collapsedSections[arr.key] ? 'pi-chevron-right' : 'pi-chevron-down'
              "
            />
            <span class="detail-section-title !m-0">{{ arr.label }}</span>
            <Tag :value="String(arr.count)" severity="secondary" rounded />
          </div>
        </button>
        <div v-show="!collapsedSections[arr.key]" class="px-4 pb-4">
          <DataTable
            v-if="arr.isObjectArray"
            :value="arr.items"
            :paginator="arr.items.length > 10"
            :rows="10"
            striped-rows
            class="p-datatable-sm"
            size="small"
          >
            <Column
              v-for="col in arr.columns"
              :key="col"
              :field="col"
              :header="formatLabel(col)"
            >
              <template #body="slotProps">
                <template
                  v-if="
                    typeof slotProps.data[col] === 'object' &&
                    slotProps.data[col] !== null &&
                    !Array.isArray(slotProps.data[col])
                  "
                >
                  <span
                    v-if="getCellRelationRoute(arr.key, col, slotProps.data[col])"
                    class="cell-link"
                    role="link"
                    tabindex="0"
                    @click.stop="
                      navigateToCellRelation(arr.key, col, slotProps.data[col])
                    "
                    @keydown.enter.stop.prevent="
                      navigateToCellRelation(arr.key, col, slotProps.data[col])
                    "
                  >
                    {{
                      formatNestedObject(
                        slotProps.data[col],
                        cellSchema(arrayItemSchema(arr.key), col),
                      )
                    }}
                  </span>
                  <template v-else>
                    {{
                      formatNestedObject(
                        slotProps.data[col],
                        cellSchema(arrayItemSchema(arr.key), col),
                      )
                    }}
                  </template>
                </template>
                <template v-else>
                  {{ slotProps.data[col] ?? "-" }}
                </template>
              </template>
            </Column>
            <Column
              v-if="arr.items[0] && 'id' in arr.items[0]"
              header=""
              body-style="text-align: end"
            >
              <template #body="slotProps">
                <Button
                  icon="pi pi-external-link"
                  text
                  rounded
                  size="small"
                  severity="secondary"
                  @click="navigateToArrayEntity(arr.key, slotProps.data)"
                />
              </template>
            </Column>
          </DataTable>
          <DataTable
            v-else
            :value="arr.primitiveItems"
            :paginator="arr.primitiveItems.length > 10"
            :rows="10"
            striped-rows
            class="p-datatable-sm"
            size="small"
          >
            <Column :header="formatLabel(pluralize.singular(arr.key))" field="id">
              <template #body="slotProps">
                <SharedCopyableField
                  :value="slotProps.data.id"
                  class="text-sm font-mono"
                />
              </template>
            </Column>
            <Column
              v-if="arr.entityRoute"
              header=""
              body-style="text-align: end; width: 3rem"
            >
              <template #body="slotProps">
                <Button
                  icon="pi pi-external-link"
                  text
                  rounded
                  size="small"
                  severity="secondary"
                  @click="navigateToArrayEntity(arr.key, slotProps.data)"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <SharedEntityDrawer
      v-if="isDrawerVisible && data"
      v-model:visible="isDrawerVisible"
      :schema="entitySchema"
      :type="drawerType"
      :data="data"
    />
  </div>
</template>

<style scoped>
.detail-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-field-label {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  margin-bottom: 0.25rem;
}

.detail-array-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  cursor: pointer;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--p-content-border-color);
  text-align: left;
  color: var(--p-text-color);
  transition: background-color 0.2s;
}

.detail-array-header:hover {
  background-color: var(--p-content-hover-background);
}

.cell-link {
  color: var(--p-primary-color);
  cursor: pointer;
  text-decoration: none;
  outline: none;
}

.cell-link:hover,
.cell-link:focus-visible {
  text-decoration: underline;
}
</style>
